import { NetworkStatus, useQuery } from '@apollo/client';
import { PlusIcon } from '@heroicons/react/24/outline';
import { Tooltip } from '@mui/material';
import { gql } from '__generated__/gql';
import { DealStatusEnumType, SyndicateTypeEnumType } from '__generated__/graphql';
import { useNavigate, useParams } from 'react-router-dom';

import SidebarLayout from 'layouts/SidebarLayout';
import { SidebarLayoutContent } from 'layouts/SidebarLayout';
import { SidebarLayoutNavigation } from 'layouts/SidebarLayout';

import DataTable from 'components/DataTable';
import ErrorMessage from 'components/ErrorMessage';
import FilterBar from 'components/FilterBar';

import Button from 'primitives/Button';
import LoadingIndicator from 'primitives/LoadingIndicator';
import TextField from 'primitives/TextField';

import debounce from 'utils/debounce';
import statusEnumToReadable from 'utils/enums/status-enum-to-readable';

const GET_FUND_SYNDICATE = gql(`
  query Fund($fundIdFor: ID!) {
    fund(id: $fundIdFor) {
      id
      syndicate {
        id
      }
    }
  }
`);

const DEALS_QUERY = gql(`
  query GetDealsOfFundSyndicate(
    $syndicateId: ID!
    $cursor: ID
    $limit: Int
    $filters: DealsFilterType
  ) {
    syndicate(id: $syndicateId) {
      id
      status
      type
      statistics {
        id
        dealStatisticsBreakdownByDealStatus {
          status
          numberOfDeals
        }
      }
      deals(cursor: $cursor, limit: $limit, filters: $filters) {
        nodes {
          id
          companyInformation {
            id
            company {
              id
              name
              image
            }
          }
          dealTerms {
            id
            fundingRoundName
          }
          allocation
          statistics {
            id
            totalWiredAmount
          }
          closingDate
          status
          createdAt
        }
        pageInfo {
          hasNextPage
          cursor
          totalCount
        }
      }
    }
  }
`);

const FundDeals = () => {
  const { fundId } = useParams<{ fundId: string }>() as { fundId: string };
  const navigate = useNavigate();

  const { data: fundData, loading: fundLoading } = useQuery(GET_FUND_SYNDICATE, {
    variables: {
      fundIdFor: fundId,
    },
  });

  const syndicateId = fundData?.fund.syndicate?.id || '';

  const { loading, error, data, refetch, fetchMore, variables, networkStatus } = useQuery(
    DEALS_QUERY,
    {
      variables: {
        syndicateId,
        limit: 50,
        cursor: undefined,
        filters: {
          status: undefined,
          text: '',
        },
      },
      skip: !syndicateId,
      notifyOnNetworkStatusChange: true,
    }
  );

  const renderContent = () => {
    if (loading || fundLoading) return <LoadingIndicator />;

    if (error || !data) return <ErrorMessage error={error} refetch={refetch} />;

    const syndicate = data.syndicate;
    const deals = syndicate.deals.nodes;
    const statistics = syndicate.statistics;
    const pageInfo = syndicate.deals.pageInfo;

    return (
      <>
        <div className="sm:flex sm:items-center justify-between">
          <FilterBar
            filters={[
              {
                label: 'All',
                value: undefined,
              },
              ...statistics.dealStatisticsBreakdownByDealStatus
                .filter(({ status }) =>
                  ['DRAFT', 'UNDER_REVIEW', 'RAISING', 'WIRED', 'CANCELLED', 'POOLING'].includes(
                    status as DealStatusEnumType
                  )
                )
                .map(({ status, numberOfDeals }) => ({
                  label: `${statusEnumToReadable(status)?.label} (${numberOfDeals})`,
                  value: status,
                })),
            ]}
            currentValue={variables?.filters?.status}
            onSelect={value => {
              refetch({
                filters: {
                  // @ts-ignore
                  status: value as DealStatusEnumType,
                },
              });
            }}
          />
          <Tooltip
            title={
              data?.syndicate.status === 'UNDER_REVIEW'
                ? 'Your syndicate is still under review'
                : ''
            }
          >
            <span>
              <Button
                disabled={data?.syndicate.status === 'UNDER_REVIEW'}
                onClick={() => navigate(`/fund-dashboard/${syndicateId}/create-deal`)}
                leadingIcon={<PlusIcon />}
              >
                Create New Deal
              </Button>
            </span>
          </Tooltip>
        </div>
        <div className="flex justify-between items-end">
          <TextField
            placeholder="Search for a deal"
            onChange={debounce(e => {
              refetch({
                filters: {
                  ...variables?.filters,
                  text: e.target.value,
                },
              });
            }, 500)}
          />
          <span className="text-sm text-gray-500">
            {deals.length} of {pageInfo.totalCount} deals
          </span>
        </div>
        <DataTable
          data={deals}
          onClick={deal => {
            if (deal.status === 'UNDER_OPS_REVIEW') {
              navigate(`/fund-dashboard/${syndicateId}/create-deal/${deal.id}/under-review`);
            } else {
              navigate(`/fund-dashboard/${fundId}/deals-of-fund/${deal.id}`);
            }
          }}
          columns={[
            {
              label: 'Company',
              fieldName: 'companyInformation.company',
              type: 'IMAGE_WITH_NAME',
            },
            {
              label: 'Status',
              fieldName: 'status',
              type: 'STATUS',
            },
            {
              label: 'Round Name',
              fieldName: 'dealTerms.fundingRoundName',
              type: 'FUNDING_ROUND',
            },
            {
              label: 'Allocation',
              fieldName: 'allocation',
              type: 'CURRENCY',
            },
            {
              label: 'Raised',
              fieldName: 'statistics.totalWiredAmount',
              type: 'CURRENCY',
            },
            {
              label: 'Closing Date',
              fieldName: 'closingDate',
              type: 'DATE',
            },
            {
              label: 'Created At',
              fieldName: 'createdAt',
              type: 'DATE',
            },
          ]}
          hasNextPage={pageInfo.hasNextPage}
          filterLoading={networkStatus === NetworkStatus.setVariables}
          paginationLoading={loading}
          onLoadMore={() =>
            fetchMore({
              variables: {
                cursor: pageInfo.cursor,
              },
            })
          }
          emptyListTitle="You haven't made any investments yet"
          emptyListDescription="Investments will appear here once they are made."
        />
      </>
    );
  };

  return (
    <SidebarLayout>
      <SidebarLayoutNavigation title="Deals" subTitle="View the deals of your fund here" />
      <SidebarLayoutContent>{renderContent()}</SidebarLayoutContent>
    </SidebarLayout>
  );
};

export default FundDeals;
